/**
 * Каркас страницы: вход, шапка, выход, единый разбор `401`/`403`.
 *
 * Всё, что здесь есть, одинаково у всех трёх сервисов, и потому лежит в
 * каркасе, а не в экране:
 *
 * - пока не понятно, кто пришёл, показывается загрузка, а не пустая
 *   страница, которая через секунду дёрнется;
 * - `401` — не ошибка, а «вы ещё не входили»: показывается экран входа;
 * - `403` где угодно — единый экран отказа §6.4, без причины;
 * - шапка с именем вошедшего, переключателем темы и выходом.
 *
 * Что показывать внутри — дело сервиса: содержимое приходит функцией,
 * которой каркас передаёт уже проверенного пользователя.
 */

import { useCallback, useEffect, useState } from 'react';
import type { ReactNode } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { ApiError, api } from './api';
import type { LoginConfig, Me } from './api';
import { setRestaurantTimeZone } from './format';
import { isMiniApp } from './miniapp';
import { Denied, ErrorState, Loading } from './states';
import { LoginScreen } from './TelegramLogin';
import type { TelegramAuthResult } from './TelegramLogin';
import { ThemeToggle } from './theme';
import { Button } from './ui';

/**
 * Кто вошёл. `fetchMe` подменяется сервисом, у которого свой API: Mini App
 * обучающего сервиса спрашивает `/api/study/me` у своего процесса, а не
 * `/api/auth/me` у `platform-api`, — потому что подпись `initData`
 * проверяется токеном того бота, из которого приложение открыто (§6.1), и
 * знает его только процесс сервиса.
 */
export function useSession(fetchMe: () => Promise<Me> = api.me) {
  return useQuery<Me, ApiError>({ queryKey: ['me'], queryFn: fetchMe });
}

export function AppShell({
  title,
  children,
  fetchMe = api.me,
}: {
  title: string;
  children: (session: Me) => ReactNode;
  /** Откуда каркас узнаёт, кто вошёл. По умолчанию — `platform-api`. */
  fetchMe?: () => Promise<Me>;
}) {
  const queryClient = useQueryClient();
  const [loginPending, setLoginPending] = useState(false);
  const [loginProblem, setLoginProblem] = useState<string | undefined>();

  // Внутри Telegram кнопка входа не нужна — человек уже вошёл, — а
  // значит, не нужен и запрос за её настройками: у процесса сервиса
  // адреса `/api/auth/config` может не быть вовсе.
  const miniApp = isMiniApp();

  const config = useQuery<LoginConfig, ApiError>({
    queryKey: ['login-config'],
    queryFn: api.loginConfig,
    staleTime: Infinity,
    enabled: !miniApp,
  });

  const session = useSession(fetchMe);

  useEffect(() => {
    if (config.data?.restaurant_tz) {
      setRestaurantTimeZone(config.data.restaurant_tz);
    }
  }, [config.data?.restaurant_tz]);

  const onAuth = useCallback(
    (user: TelegramAuthResult) => {
      setLoginPending(true);
      setLoginProblem(undefined);
      api
        .loginWithTelegram(user as unknown as Record<string, unknown>)
        .then((me) => {
          queryClient.setQueryData(['me'], me);
          return queryClient.invalidateQueries({ queryKey: ['me'] });
        })
        .catch((error: unknown) => {
          if (error instanceof ApiError && error.isForbidden) {
            // §6.4: причина не называется. Формулировка одна и та же и
            // для незнакомого платформе человека, и для знакомого без
            // доступа — иначе перебором выясняется состав пользователей.
            setLoginProblem(
              'Вход выполнить не удалось. Если вы новый сотрудник, запросите доступ через бота.',
            );
          } else if (error instanceof ApiError && error.status === 503) {
            setLoginProblem(
              'Вход в веб на этом сервере ещё не настроен — сообщите администратору.',
            );
          } else {
            setLoginProblem('Телеграм подтвердил вход, но сервер его не принял. Попробуйте ещё раз.');
          }
        })
        .finally(() => setLoginPending(false));
    },
    [queryClient],
  );

  const onLogout = useCallback(() => {
    api
      .logout()
      .catch(() => undefined) // выход не может «не получиться» для человека
      .finally(() => queryClient.clear());
  }, [queryClient]);

  if ((config.isPending && !miniApp) || session.isPending) {
    return <Loading />;
  }

  if (session.error?.isUnauthorized && miniApp) {
    // В Mini App `401` означает одно: подписанные данные Telegram не
    // приняты сервером — чаще всего они просто устарели (§6.1, срок
    // свежести). Показывать кнопку «войти через Telegram» человеку,
    // который находится внутри Telegram, бессмысленно.
    return (
      <div className="shell">
        <main className="shell-main">
          <ErrorState
            title="Сессия Telegram устарела"
            text="Закройте приложение и откройте его заново из бота."
            onRetry={() => session.refetch()}
          />
        </main>
      </div>
    );
  }

  if (session.error?.isUnauthorized) {
    return (
      <div className="shell">
        <header className="shell-header">
          <span className="shell-header__title">Тоскана</span>
          <span className="shell-header__spacer" />
          <ThemeToggle />
        </header>
        <main className="shell-main">
          <LoginScreen
            botUsername={config.data?.bot_username ?? ''}
            loginAvailable={config.data?.login_available ?? false}
            onAuth={onAuth}
            pending={loginPending}
            failedText={loginProblem}
          />
        </main>
      </div>
    );
  }

  if (session.error?.isForbidden) {
    return <Denied botUsername={config.data?.bot_username} />;
  }

  if (session.error || !session.data) {
    return <ErrorState onRetry={() => session.refetch()} />;
  }

  const me = session.data;

  return (
    <div className="shell">
      <header className="shell-header">
        <span className="shell-header__title">{title}</span>
        <span className="shell-header__spacer" />
        <span className="faint">{me.full_name ?? 'Без имени'}</span>
        <ThemeToggle />
        {/* Выхода в Mini App нет: выходить не из чего — сессии нет
            (§6.2), а «выйти из Telegram» кнопка в приложении не умеет. */}
        {miniApp ? null : (
          <Button small variant="quiet" onClick={onLogout}>
            Выйти
          </Button>
        )}
      </header>
      <main className="shell-main">{children(me)}</main>
    </div>
  );
}
